import { useMemo, useCallback } from 'react'

import { TagStatus } from '../api/BMDataTypes'
import { STATUSES } from './statuses.model'
import { Domain } from 'Types/Domain'
import { useBMData } from '../api/useBMData'

type StatusKey = keyof typeof STATUSES

export const useGetBMProtectionStatus = (
  domains: Domain[] = []
): Record<string, StatusKey | null> => {
  const { tags, tagStatus } = useBMData()

  const tagStatusMap = useMemo(() => {
    return (tagStatus ?? []).reduce((acc, status) => {
      acc[status.domain] = status
      return acc
    }, {} as Record<string, TagStatus>)
  }, [tagStatus])

  const determineStatus = useCallback(
    (domain: Domain): StatusKey | null => {
      if (!domain?.id) return null

      const domainHasData = tagStatusMap?.[domain.address]?.active ?? false
      const { wordpress: isWordpress, botZappingPluginActivated, tagId } = domain

      const monitoringObj = tags?.find((tag) => tag.searchId === tagId) ?? null
      const isMonitoring = monitoringObj?.monitoringMode?.isMonitoring ?? false

      if (!isWordpress) return 'wordpressNotRecognized'
      if ((botZappingPluginActivated || domainHasData) && !isMonitoring) return 'protected'
      if (botZappingPluginActivated && isMonitoring) return 'monitoringOnly'

      return 'notProtected'
    },
    [tags, tagStatusMap]
  )

  return useMemo(() => {
    return domains.reduce<Record<string, StatusKey | null>>((acc, domain) => {
      acc[domain.address] = determineStatus(domain)
      return acc
    }, {})
  }, [domains, determineStatus])
}
